import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { del, get, post, withPath, type ApiPath } from '@app/api/typed';
import type {
  AdmissionApply,
  AdmissionPreview,
  AdmissionSettings,
  AdmissionSummary,
  AdmissionToken,
  AdmissionVersion,
  SiteAdmission,
} from './SiteAdmissionModel';

/**
 * Hooks da admissão no cluster (aim-xsk.8). Toda mutação invalida a página e o resumo da lista: o chip da coluna
 * «Admissão» lê o que ficou gravado, e é a mutação que o muda.
 */
export const admissionKeys = {
  all: ['siteAdmission'] as const,
  summaries: () => ['siteAdmission', 'summaries'] as const,
  site: (id: string) => ['siteAdmission', 'site', id] as const,
  versions: (id: string) => ['siteAdmission', 'versions', id] as const,
};

function route<P extends ApiPath>(template: P, id: string): P {
  return withPath(template, { id }) as P;
}

/** O resumo da coluna «Admissão». Só o admin lê (`x-roles: [admin]`); quem chama passa `enabled`. */
export function useAdmissionSummaries(enabled: boolean): UseQueryResult<Map<string, AdmissionSummary>, ApiError> {
  return useQuery<Map<string, AdmissionSummary>, ApiError>({
    queryKey: admissionKeys.summaries(),
    queryFn: async () => {
      const data = await get('/api/v1/sites/admissions');
      return new Map(data.items.map((item) => [item.site_id, item]));
    },
    enabled,
  });
}

export function useSiteAdmission(siteId: string | undefined): UseQueryResult<SiteAdmission, ApiError> {
  return useQuery<SiteAdmission, ApiError>({
    queryKey: admissionKeys.site(siteId ?? ''),
    queryFn: () => get(route('/api/v1/sites/{id}/admission', siteId ?? '')),
    enabled: Boolean(siteId),
  });
}

export function useAdmissionVersions(siteId: string, enabled: boolean): UseQueryResult<AdmissionVersion[], ApiError> {
  return useQuery<AdmissionVersion[], ApiError>({
    queryKey: admissionKeys.versions(siteId),
    queryFn: async () => (await get(route('/api/v1/sites/{id}/admission/versions', siteId))).items,
    enabled,
  });
}

function useInvalidate(siteId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: admissionKeys.site(siteId) });
    void queryClient.invalidateQueries({ queryKey: admissionKeys.summaries() });
    void queryClient.invalidateQueries({ queryKey: admissionKeys.versions(siteId) });
  };
}

export function useCreateAdmissionToken(siteId: string): UseMutationResult<AdmissionToken, ApiError, void> {
  const invalidate = useInvalidate(siteId);
  return useMutation<AdmissionToken, ApiError, void>({
    mutationFn: () => post(route('/api/v1/sites/{id}/admission/token', siteId)),
    onSuccess: invalidate,
  });
}

export function useRevokeAdmissionToken(siteId: string): UseMutationResult<unknown, ApiError, void> {
  const invalidate = useInvalidate(siteId);
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => del(route('/api/v1/sites/{id}/admission/token', siteId)),
    onSuccess: invalidate,
  });
}

export interface RegisterVars {
  endpoint: string;
  token: string;
}

export function useRegisterAdmission(siteId: string): UseMutationResult<SiteAdmission, ApiError, RegisterVars> {
  const invalidate = useInvalidate(siteId);
  return useMutation<SiteAdmission, ApiError, RegisterVars>({
    mutationFn: (body) => post(route('/api/v1/sites/{id}/admission/register', siteId), { body }),
    onSuccess: invalidate,
  });
}

/** A prévia não grava nada: não invalida. */
export function usePreviewAdmission(siteId: string): UseMutationResult<AdmissionPreview, ApiError, AdmissionSettings> {
  return useMutation<AdmissionPreview, ApiError, AdmissionSettings>({
    mutationFn: (settings) => post(route('/api/v1/sites/{id}/admission/preview', siteId), { body: { settings } }),
  });
}

export function useApplyAdmission(siteId: string): UseMutationResult<AdmissionVersion, ApiError, AdmissionApply> {
  const invalidate = useInvalidate(siteId);
  return useMutation<AdmissionVersion, ApiError, AdmissionApply>({
    mutationFn: (body) => post(route('/api/v1/sites/{id}/admission/versions', siteId), { body }),
    onSuccess: invalidate,
  });
}

export function useRetryAdmission(siteId: string): UseMutationResult<AdmissionVersion, ApiError, void> {
  const invalidate = useInvalidate(siteId);
  return useMutation<AdmissionVersion, ApiError, void>({
    mutationFn: () => post(route('/api/v1/sites/{id}/admission/retry', siteId)),
    onSuccess: invalidate,
  });
}

export function useRefreshAdmission(siteId: string): UseMutationResult<SiteAdmission, ApiError, void> {
  const invalidate = useInvalidate(siteId);
  return useMutation<SiteAdmission, ApiError, void>({
    mutationFn: () => post(route('/api/v1/sites/{id}/admission/refresh', siteId)),
    onSuccess: invalidate,
  });
}
